import { ISelectOption } from './select';

/**
 * The two-letter state codes a US postal address can carry.
 *
 * A free-text State field is the one place in a payer address where a typo is silent: "Tenn"
 * or "Utha" looks filled in, passes any length check, and produces an address the post office
 * cannot deliver to — for a category whose whole purpose is reaching a payer on paper. Ken
 * Coleta asked for the dropdown back on 2026-08-31: *"Can we also make the state field a drop
 * down? that is how we originally set it up."*
 *
 * Codes rather than names, because that is what the API stores and what a claim carries.
 *
 * Every code the USPS accepts in this position: the fifty states, DC, the five inhabited
 * territories, the Armed Forces codes (AA/AE/AP — TRICARE and military plans use APO/FPO
 * addresses), and the Freely Associated States and outlying islands (FM/MH/PW/UM).
 *
 * The list is exhaustive on purpose. Constraining a field is only an improvement while it can
 * still express every real value; the moment it cannot, it stops being a guard and becomes the
 * reason a legitimate address cannot be entered — which is worse than the typo it prevents.
 */
export const US_STATE_CODES: string[] = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
    'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
    'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
    'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
    'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI',
    'WY',
    // Territories: a payer in San Juan is not an edge case.
    'AS', 'GU', 'MP', 'PR', 'VI',
    // Armed Forces — TRICARE and military health plans use APO/FPO addresses, and these are
    // the codes the USPS expects for them.
    'AA', 'AE', 'AP',
    // Freely Associated States and the minor outlying islands. Deliverable, and rare enough
    // that nobody would notice they were missing until the one payer that needs one.
    'FM', 'MH', 'PW', 'UM',
];

/** The same list as select options, with a blank first entry so the field can be cleared. */
export const US_STATE_OPTIONS: ISelectOption[] = [
    { value: undefined, label: '' },
    ...US_STATE_CODES.map(code => ({ value: code, label: code })),
];
